import React, { useState, useEffect, useContext } from "react";
import {
  Paper,
  Container,
  Stack,
  Typography,
  Chip,
  Button,
  FormControlLabel,
  Switch,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapLocationDot,
  faLocationCrosshairs,
  faRoute,
  faSatelliteDish,
} from "@fortawesome/free-solid-svg-icons";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip as EtiquetaMapa,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment/moment";
import { isEmpty } from "lodash";

import { DataContext } from "../../../context/DataContext";
import { getToken } from "../../../api/Account";
import { getLastPositionsAsync } from "../../../api/Positions";
import { getRuta, simpleMessage } from "../../../helpers/Helpers";
import {
  esIncidencia,
  estadoSenal,
  haceCuanto,
  hoyEnNicaragua,
  minutosDesde,
  REFRESCO_MS,
  CENTRO_POR_DEFECTO,
} from "../../../helpers/Seguimiento";
import { AjustarVista, CentrarEn, iconoGestor } from "../../../components/MapaSeguimiento";
import NoData from "../../../components/NoData";

/**
 * Como se lee el estado de un gestor. Un corte manda sobre la antiguedad de la señal: que
 * el ultimo dato sea de hace dos minutos no significa nada bueno si ese dato dice que
 * apagaron la ubicacion.
 */
const estadoDe = (gestor) => {
  if (gestor.ultima === null) {
    return {
      texto: "Sin registros hoy",
      color: "#757575",
      chip: "default",
      latiendo: false,
      minutos: null,
    };
  }

  const minutos = minutosDesde(gestor.ultima.capturedAt);
  const senal = estadoSenal(minutos);

  if (esIncidencia(gestor.ultima)) {
    return {
      texto: `Sin señal ${haceCuanto(minutos)}`,
      color: "#d32f2f",
      chip: "error",
      latiendo: false,
      minutos,
    };
  }

  return {
    texto: haceCuanto(minutos),
    color: senal.color,
    chip: senal.chip,
    latiendo: senal.viva,
    minutos,
  };
};

// En el mapa va el nombre corto: los nombres completos de cuatro palabras se pisan entre
// ellos en cuanto dos gestores trabajan la misma zona.
const nombreCorto = (fullName) => fullName.split(" ").slice(0, 2).join(" ");

const conPosicion = (gestor) =>
  gestor.ultima !== null && gestor.ultima.latitude !== null;

const GestoresEnVivo = () => {
  const [gestores, setGestores] = useState([]);
  const [consultado, setConsultado] = useState(false);
  const [actualizado, setActualizado] = useState(null);
  const [enVivo, setEnVivo] = useState(true);
  const [foco, setFoco] = useState({ posicion: null, senal: 0 });
  const [ajuste, setAjuste] = useState(0);

  const { setIsLoading, setIsUnautorized } = useContext(DataContext);
  const token = getToken();
  const ruta = getRuta();

  // El refresco automatico no levanta la pantalla de carga: taparia el mapa cada minuto sin
  // que el usuario haya pedido nada.
  const consultar = async (silencioso) => {
    if (!silencioso) {
      setIsLoading(true);
    }

    const result = await getLastPositionsAsync(token);

    if (!silencioso) {
      setIsLoading(false);
    }

    if (!result.statusResponse) {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      // Un fallo de red no se anuncia en los refrescos: el siguiente intento entra en un
      // minuto y una alerta por cada uno seria insoportable.
      if (!silencioso) {
        simpleMessage(result.error, "error");
      }
      return;
    }

    setGestores(result.data.result);
    setActualizado(new Date());
    setConsultado(true);

    // Solo se reencuadra en la consulta que el usuario pidio. En los refrescos el mapa se
    // queda donde lo dejo: mover la vista cada minuto haria imposible seguir a nadie.
    if (!silencioso) {
      setAjuste((valor) => valor + 1);
    }
  };

  useEffect(() => {
    consultar(false);
  }, []);

  useEffect(() => {
    if (!enVivo) {
      return undefined;
    }

    const temporizador = setInterval(() => consultar(true), REFRESCO_MS);
    return () => clearInterval(temporizador);
  }, [enVivo]);

  const enMapa = gestores.filter(conPosicion);
  const posiciones = enMapa.map((g) => [g.ultima.latitude, g.ultima.longitude]);

  // Los que no reportaron nada van al final de la lista: no hay donde buscarlos en el mapa,
  // pero tienen que estar a la vista, porque esa ausencia es la que hay que explicar.
  const conReporte = gestores.filter((g) => g.ultima !== null);
  const sinReporte = gestores.filter((g) => g.ultima === null);

  const estados = new Map(gestores.map((g) => [g.userId, estadoDe(g)]));

  const enLinea = conReporte.filter(
    (g) => estados.get(g.userId).latiendo
  ).length;

  const verRecorrido = (gestor) => {
    window.open(`${ruta}/r-rutagestor/${gestor.userId}/${hoyEnNicaragua()}`);
  };

  const centrarEnGestor = (gestor) => {
    if (!conPosicion(gestor)) {
      simpleMessage("Ese gestor no tiene una ubicación que mostrar", "info");
      return;
    }
    setFoco({
      posicion: [gestor.ultima.latitude, gestor.ultima.longitude],
      senal: foco.senal + 1,
    });
  };

  const filaGestor = (gestor) => {
    const estado = estados.get(gestor.userId);
    const bateria =
      gestor.ultima === null ||
      gestor.ultima.batteryLevel === null ||
      gestor.ultima.batteryLevel === undefined
        ? null
        : `${gestor.ultima.batteryLevel}%`;

    return (
      <React.Fragment key={gestor.userId}>
        <ListItemButton onClick={() => centrarEnGestor(gestor)}>
          <ListItemText
            primary={
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography fontWeight="bold">{gestor.fullName}</Typography>
                <Chip size="small" label={estado.texto} color={estado.chip} />
              </Stack>
            }
            secondary={
              <>
                {/* El rol va primero: en el mismo mapa conviven gestores,
                desembolsadores y promotores, y lo que se espera del recorrido de
                cada uno es distinto. */}
                {gestor.rolName ? `${gestor.rolName} · ` : ""}
                {gestor.ultima === null
                  ? "No ha enviado ubicación en la jornada"
                  : `${moment(gestor.ultima.capturedAt).format("hh:mm A")}${
                      gestor.ultima.detalle ? ` · ${gestor.ultima.detalle}` : ""
                    }${bateria === null ? "" : ` · Batería ${bateria}`}`}
              </>
            }
          />
          <Button
            size="small"
            startIcon={<FontAwesomeIcon icon={faRoute} />}
            onClick={(e) => {
              e.stopPropagation();
              verRecorrido(gestor);
            }}
            style={{ borderRadius: 20, color: "#1769aa" }}
          >
            Recorrido
          </Button>
        </ListItemButton>
        <Divider />
      </React.Fragment>
    );
  };

  return (
    <div>
      <Container maxWidth="xl">
        <Paper style={{ padding: 20 }}>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <Typography color={"#00a152"} variant="h4" fontWeight={"bold"}>
              <FontAwesomeIcon icon={faMapLocationDot} />
            </Typography>
            <Typography color={"#00a152"} variant="h4" fontWeight={"bold"}>
              Gestores en Vivo
            </Typography>
          </Stack>

          <Typography variant="subtitle1" textAlign={"center"}>
            {moment().format("DD/MM/YYYY")}
            {actualizado === null
              ? ""
              : ` · actualizado ${moment(actualizado).format("hh:mm:ss A")}`}
          </Typography>

          <hr />

          {consultado && isEmpty(gestores) ? (
            <NoData />
          ) : (
            <>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                marginTop={2}
                marginBottom={2}
                flexWrap="wrap"
              >
                <Chip
                  icon={<FontAwesomeIcon icon={faSatelliteDish} />}
                  label={`${enLinea} de ${gestores.length} en línea`}
                  color={enLinea === 0 ? "default" : "success"}
                />
                {isEmpty(sinReporte) ? null : (
                  <Chip
                    label={`${sinReporte.length} sin registros hoy`}
                    color="error"
                    variant="outlined"
                  />
                )}
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                alignItems="center"
                marginBottom={2}
                flexWrap="wrap"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={enVivo}
                      onChange={(e) => setEnVivo(e.target.checked)}
                      color="success"
                    />
                  }
                  label="Actualizar cada minuto"
                />

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FontAwesomeIcon icon={faLocationCrosshairs} />}
                  disabled={isEmpty(posiciones)}
                  onClick={() => setAjuste((valor) => valor + 1)}
                  style={
                    isEmpty(posiciones)
                      ? { borderRadius: 20 }
                      : {
                          borderRadius: 20,
                          color: "#1769aa",
                          borderColor: "#1769aa",
                        }
                  }
                >
                  Ver todos
                </Button>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper variant="outlined" style={{ borderRadius: 15 }}>
                    <List dense>
                      {conReporte.map((gestor) => filaGestor(gestor))}
                      {sinReporte.map((gestor) => filaGestor(gestor))}
                    </List>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={8}>
                  <MapContainer
                    center={
                      isEmpty(posiciones) ? CENTRO_POR_DEFECTO : posiciones[0]
                    }
                    zoom={13}
                    style={{
                      height: "70vh",
                      width: "100%",
                      borderRadius: 15,
                    }}
                    scrollWheelZoom
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    <AjustarVista posiciones={posiciones} senal={ajuste} />
                    <CentrarEn posicion={foco.posicion} senal={foco.senal} />

                    {enMapa.map((gestor) => {
                      const estado = estados.get(gestor.userId);

                      return (
                        <Marker
                          key={gestor.userId}
                          position={[
                            gestor.ultima.latitude,
                            gestor.ultima.longitude,
                          ]}
                          icon={iconoGestor(estado.color, estado.latiendo)}
                        >
                          <EtiquetaMapa
                            permanent
                            direction="right"
                            offset={[14, -14]}
                          >
                            {nombreCorto(gestor.fullName)}
                          </EtiquetaMapa>
                          <Popup>
                            <strong style={{ color: estado.color }}>
                              {gestor.fullName}
                            </strong>
                            {gestor.rolName ? (
                              <>
                                <br />
                                <small>{gestor.rolName}</small>
                              </>
                            ) : null}
                            <br />
                            {moment(gestor.ultima.capturedAt).format("hh:mm A")}{" "}
                            · {estado.texto}
                            {gestor.ultima.detalle ? (
                              <>
                                <br />
                                {gestor.ultima.detalle}
                              </>
                            ) : null}
                            <br />
                            <small>
                              {gestor.ultima.accuracy > 0
                                ? `Precisión: ${Math.round(
                                    gestor.ultima.accuracy
                                  )} m`
                                : "Sin precisión reportada"}
                              {gestor.ultima.batteryLevel === null ||
                              gestor.ultima.batteryLevel === undefined
                                ? ""
                                : ` · Batería: ${gestor.ultima.batteryLevel}%`}
                            </small>
                            <br />
                            <Button
                              size="small"
                              startIcon={<FontAwesomeIcon icon={faRoute} />}
                              onClick={() => verRecorrido(gestor)}
                              style={{ color: "#1769aa" }}
                            >
                              Ver recorrido del día
                            </Button>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MapContainer>
                </Grid>
              </Grid>
            </>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default GestoresEnVivo;
